import { router } from "@inertiajs/react"
import { Pencil, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog"
import { cn } from "@/lib/utils"
import type { TaskRecord } from "@/components/tasks/types"

type TaskListProps = {
  readonly tasks: readonly TaskRecord[]
  readonly emptyText?: string
  readonly editable?: boolean
}

export function TaskList({
  tasks,
  emptyText = "No tasks.",
  editable = true,
}: TaskListProps) {
  if (tasks.length === 0) {
    return <p className="text-sm text-ink-muted">{emptyText}</p>
  }

  return (
    <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-page">
      {tasks.map((task) => (
        <TaskRow key={task.id} task={task} editable={editable} />
      ))}
    </ul>
  )
}

function TaskRow({
  task,
  editable,
}: {
  readonly task: TaskRecord
  readonly editable: boolean
}) {
  const completed = Boolean(task.completed_at)
  const overdue = !completed && isOverdue(task.due_on)

  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <Checkbox
        className="mt-1"
        checked={completed}
        onChange={() => {
          router.post(`/tasks/${task.id}/toggle`, {}, { preserveScroll: true })
        }}
        aria-label={completed ? "Mark task incomplete" : "Mark task complete"}
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-ink-display",
            completed && "text-ink-muted line-through",
          )}
        >
          {task.title}
        </div>
        {task.due_on && (
          <div
            className={cn(
              "mt-1 text-sm text-ink-muted",
              overdue && "text-danger-display",
            )}
          >
            Due {formatDate(task.due_on)}
          </div>
        )}
        {task.notes && (
          <p className="mt-1 text-sm text-ink-muted">{task.notes}</p>
        )}
      </div>
      {editable && (
        <div className="flex shrink-0 gap-1">
          <TaskFormDialog
            task={task}
            trigger={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Edit task"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Delete task"
            onClick={() => {
              router.delete(`/tasks/${task.id}`, { preserveScroll: true })
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </li>
  )
}

function isOverdue(dueOn: string | null) {
  if (!dueOn) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(dueOn) < today
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
}
